/* ==========================================================================
   INDUSTRIES DATA - copy for the per-industry landing pages + /industries index.
   Each entry drives /industries/<slug> via IndustryPage.js.
   `icon` and solution `icon` values map to lucide-react names in IndustryPage.js.
   Brand: white / black / gold. No em-dashes anywhere.
   ========================================================================== */

export const INDUSTRIES = [
  {
    slug: 'restaurants',
    name: 'Restaurants',
    icon: 'UtensilsCrossed',
    blurb: 'Direct orders, synced reservations, and an AI host that answers during the rush.',
    seoTitle: 'Restaurant Websites & AI Booking - Versa Launch',
    seoDescription:
      'Fill more tables and answer every call during the rush. Versa Launch gives restaurants commission-free ordering, OpenTable-synced reservations, and an AI phone host.',
    hero: {
      eyebrow: 'For restaurants',
      lead: 'Fill more tables. Keep more of every',
      accent: 'check',
      sub: 'A site that takes orders and reservations direct, an AI host that answers the phone during the rush, and local search ranking that puts you above the chains. You run the kitchen. We handle the rest.'
    },
    painsTitle: 'You got into food, not phone tag',
    painsSub:
      'The dinner rush is when you make your money. It should not be when you lose it. Here is what quietly costs you covers every night.',
    pains: [
      {
        title: 'The phone never stops during service',
        text: 'It rings off the hook at 7pm and nobody can grab it. Every missed call is a table you never seated and a regular who tried someone else.'
      },
      {
        title: 'Delivery apps eat 30% of the ticket',
        text: 'Third-party apps take a 30% bite out of every order and own your customer. You did the cooking. They kept the relationship and most of the margin.'
      },
      {
        title: 'Buried below the chains on Google',
        text: 'Someone searches dinner near them and finds the franchise three blocks over before they find you. Your food is better. Your listing is not.'
      }
    ],
    solutionsTitle: 'Everything your front of house needs in one place',
    solutionsSub:
      'Built for how a real restaurant runs. Direct orders, reservations that show up, and a menu you can change from your phone between shifts.',
    solutions: [
      {
        icon: 'PhoneCall',
        title: 'An AI host that answers every call',
        text: 'When the line is slammed and the host stand is empty, the AI assistant picks up, books the reservation, answers hours and menu questions, and texts the guest a confirmation. No more voicemail. No more missed covers.'
      },
      {
        icon: 'Calendar',
        title: 'Reservations synced to OpenTable',
        text: 'Take bookings right from your site and keep them in sync with OpenTable and the tools you already run. One floor plan, no double-booked tables, no host juggling two systems on a Friday night.'
      },
      {
        icon: 'ShoppingBag',
        title: 'Online ordering you actually own',
        text: 'Pickup and delivery ordering straight from your website, tied into Clover, Square, and Stripe. You keep the customer, the data, and the margin the apps were taking.'
      },
      {
        icon: 'UtensilsCrossed',
        title: 'Menu changes in under a minute',
        text: '86 the salmon, bump prices, add the weekend special. Update it once from your phone and it goes live on your site, your ordering page, and Google. No webmaster, no waiting.'
      },
      {
        icon: 'Bell',
        title: 'Fewer no-shows, fuller tables',
        text: 'Automatic text reminders go out before every reservation so guests actually show. When someone cancels, the table opens back up instead of sitting empty through the rush.'
      },
      {
        icon: 'Star',
        title: 'More 5-star reviews, more regulars',
        text: 'After a great meal we ask happy guests for a Google review and invite them back with a loyalty perk. First-time diners turn into Friday regulars, and you climb past the chains on the map.'
      }
    ],
    stats: [
      { value: '30%', label: 'typical margin kept by skipping delivery-app fees' },
      { value: '3x', label: 'more 5-star Google reviews in 90 days' },
      { value: '24/7', label: 'calls answered, even mid-rush and after close' }
    ],
    quote: {
      text: 'Friday nights we just could not get to the phone, and I knew we were losing tables. Now the AI host books them while we run the floor, and orders come straight through our own site instead of the apps. We kept way more of the check.',
      author: 'Marco R.',
      role: 'Owner, Coastal Kitchen'
    },
    cta: {
      title: 'Ready to fill more tables this weekend?',
      text: 'See your new restaurant site, AI phone host, and direct ordering set up for you. Book a quick walkthrough and we will show you exactly what it looks like for your place.'
    }
  },

  {
    slug: 'contractors',
    name: 'Contractors',
    icon: 'HardHat',
    blurb: 'Answer every call, book the job, and rank #1 in the local Google Map pack.',
    seoTitle: 'Websites & AI for Contractors - Versa Launch',
    seoDescription:
      'Stop losing jobs to missed calls. Versa Launch books contractor leads with an AI that answers 24/7, ranks you in the Google Map pack, and grows your reviews.',
    hero: {
      eyebrow: 'For contractors',
      lead: 'Win the job before',
      accent: 'the next guy',
      sub: 'You are up a ladder or under a sink, not by the phone. Versa Launch answers every call, books the job, and gets you found first on Google. The work comes to you.'
    },
    painsTitle: 'The lead came in. You were on the job.',
    painsSub:
      'Most contractors do not lose work on price. They lose it because they could not get to the phone. Here is what is quietly costing you jobs.',
    pains: [
      {
        title: 'Miss the call, lose the job',
        text: 'A homeowner with a busted water heater is not leaving a voicemail. They hit the next number on Google. Every ring you cannot answer is money walking to your competitor.'
      },
      {
        title: 'The good leads come in at night',
        text: 'People shop for contractors after dinner and on weekends, when you are off the clock. Call back the next morning and they already booked someone else.'
      },
      {
        title: 'It is hard to look bigger than you are',
        text: 'A one-truck operation and a 20-crew company look the same to a homeowner online. Without a sharp site, real reviews, and proof of your work, the bigger-looking outfit wins by default.'
      }
    ],
    solutionsTitle: 'Everything you need to stay booked',
    solutionsSub:
      'One platform that catches the lead, sells the work, and keeps your calendar full. Built for the trades, not for a tech company.',
    solutions: [
      {
        icon: 'PhoneCall',
        title: 'An AI that answers every call',
        text: 'When you cannot pick up, it does. It greets the caller, gets the job details, the address, and the timeline, then books the estimate right on your calendar. No more leads going to voicemail.'
      },
      {
        icon: 'FileText',
        title: 'Instant quote requests',
        text: 'Homeowners describe the job and request an estimate straight from your site, day or night. The details land in your pocket the second they hit send, so you quote fast and lock it in.'
      },
      {
        icon: 'Calendar',
        title: 'Online scheduling that fills the week',
        text: 'Let customers pick an estimate or service window that fits your route. No phone tag, no double-booking, and your jobs are lined up before you wake up.'
      },
      {
        icon: 'MapPin',
        title: 'Rank #1 in the Map pack',
        text: 'We set up your local SEO so you show up first when someone nearby searches your trade. Top of Google and Google Maps is where the calls come from, and that is where we put you.'
      },
      {
        icon: 'Image',
        title: 'Before and after galleries that sell',
        text: 'Your finished work is your best closer. We build project galleries that show the messy before and the clean after, so a homeowner trusts you before you ever shake their hand.'
      },
      {
        icon: 'Star',
        title: 'Reviews on autopilot',
        text: 'After every job, we automatically ask the customer for a review and make it one tap easy. More 5-star reviews means you outrank and out-trust the shop down the road, without chasing anyone.'
      }
    ],
    stats: [
      { value: '24/7', label: 'calls answered and jobs booked, even after hours' },
      { value: '3x', label: 'more 5-star reviews in the first 90 days' },
      { value: '#1', label: 'goal: top of the local Google Map pack' }
    ],
    quote: {
      text: 'I was losing two or three calls a day on the job site and never even knew it. Now the AI books the estimate while I am wrapping up a project, and I show up the next morning with the appointment already on the calendar. It paid for itself the first week.',
      author: 'Mike R.',
      role: 'Owner, Riverside Plumbing & Drain'
    },
    cta: {
      title: 'Stop letting jobs ring through to voicemail',
      text: 'See how Versa Launch books the work while you are on the job. Book a quick walkthrough and we will show you exactly what you are missing and how to catch it.'
    }
  },

  {
    slug: 'online-stores',
    name: 'Online stores',
    icon: 'ShoppingBag',
    blurb: 'A fast branded storefront that turns browsers into buyers.',
    seoTitle: 'Online Store Websites That Sell - Versa Launch',
    seoDescription:
      'A fast branded storefront, simple product and inventory management, Stripe and Square payments, recovered carts, and more online sales. Built for store owners.',
    hero: {
      eyebrow: 'For online stores',
      lead: 'A store that loads fast, looks legit, and actually',
      accent: 'makes sales',
      sub: 'Most DIY shops are slow and clunky and lose the sale at checkout. We build a fast branded storefront that customers trust and that turns browsers into buyers.'
    },
    painsTitle: 'Why your store is not selling',
    painsSub:
      'You stocked the products and took the photos. Then the website got in the way. Here is what we hear from store owners every week.',
    pains: [
      {
        title: 'A DIY site that does not convert',
        text: 'Slow on phones, an awkward checkout, and shoppers bounce before they hit buy. Traffic comes in and nobody purchases.'
      },
      {
        title: 'Carts get abandoned and never come back',
        text: 'Shoppers add items, get distracted, and leave. With no follow-up email or text, that order is gone and you never knew it existed.'
      },
      {
        title: 'You look small next to Amazon and Etsy',
        text: 'Your shop reads like a side project next to the big marketplaces, so first-time buyers will not trust it with their card. So you pay marketplace fees just to get seen.'
      }
    ],
    solutionsTitle: 'Everything your shop needs to sell more',
    solutionsSub:
      'One storefront, one place to manage it, and the automation that brings shoppers back. Built for store owners, not developers.',
    solutions: [
      {
        icon: 'Store',
        title: 'A fast branded storefront',
        text: 'A clean, on-brand shop that loads quick and feels like a real store. It earns trust on the first visit so new buyers feel safe checking out.'
      },
      {
        icon: 'Smartphone',
        title: 'Mobile shopping that feels right',
        text: 'Most of your buyers are on their phones. Product pages, cart, and checkout all work smoothly on mobile, so the sale does not die at the last tap.'
      },
      {
        icon: 'Package',
        title: 'Dead-simple products and stock',
        text: 'Add products, set prices, track stock, and run sales from one back-office. Update once and it shows everywhere. No more juggling spreadsheets.'
      },
      {
        icon: 'CreditCard',
        title: 'Stripe and Square payments',
        text: 'Take cards the way you already do. Money lands in the account you already use, with a checkout customers recognize and trust.'
      },
      {
        icon: 'ShoppingCart',
        title: 'Recover abandoned carts',
        text: 'When a shopper leaves items behind, we send an automatic email or text to bring them back. That is found money you are leaving on the table today.'
      },
      {
        icon: 'Search',
        title: 'SEO that brings real traffic',
        text: 'We get your products ranking on Google so buyers find you without paying for every click. More free traffic, more orders, fewer marketplace fees.'
      }
    ],
    stats: [
      { value: '2x', label: 'more online orders within 90 days' },
      { value: '15%', label: 'of abandoned carts typically recovered' },
      { value: '100+', label: 'local businesses growing with Versa Launch' }
    ],
    quote: {
      text: 'My old site was slow and barely sold a thing. The new store loads fast, looks professional, and the cart recovery texts alone paid for it. Orders come in while I sleep now.',
      author: 'Megan R.',
      role: 'Owner, Tidewater Goods'
    },
    cta: {
      title: "Let's build a store that sells",
      text: 'Get a fast branded storefront, simple product management, and the automation that brings shoppers back. Book a free walkthrough and see what your shop could be doing.'
    }
  },

  {
    slug: 'salons-spas',
    name: 'Salons & spas',
    icon: 'Scissors',
    blurb: '24/7 booking, fewer no-shows, and an AI that books while you work.',
    seoTitle: 'Salon & Spa Websites & Booking - Versa Launch',
    seoDescription:
      'Versa Launch gives salons and spas 24/7 online booking, an AI phone assistant that books while you are with a client, reminders that cut no-shows, and review generation.',
    hero: {
      eyebrow: 'For salons & spas',
      lead: 'Your chairs stay full, even when the front desk is',
      accent: 'in the back',
      sub: 'Clients book online any hour. An AI assistant answers the calls you miss mid-service. Automatic reminders keep no-shows off your schedule. You focus on the client in front of you.'
    },
    painsTitle: 'Slow weeks and empty chairs add up fast',
    painsSub:
      'When you are hands-on with a client, the phone keeps ringing and the calendar keeps slipping. Here is where salons and spas lose money every week.',
    pains: [
      {
        title: 'You cannot answer mid-service',
        text: 'Color is processing. You are mid-cut. Your hands are in a facial. The phone rings, goes to voicemail, and that new client books down the street.'
      },
      {
        title: 'No-shows burn the day',
        text: 'One forgotten appointment is an hour of empty chair time you never get back. A few a week and you watch real money walk out the door.'
      },
      {
        title: 'Clients forget to rebook',
        text: 'They love the result, then life gets busy and six weeks turn into three months. Without a nudge to come back, your retention quietly leaks.'
      }
    ],
    solutionsTitle: 'Everything a busy salon or spa needs to stay booked',
    solutionsSub:
      'One platform that fills your chairs, protects your schedule, and shows off the work you are proud of.',
    solutions: [
      {
        icon: 'Calendar',
        title: '24/7 online booking',
        text: 'Clients book a cut, color, facial, or massage at midnight from their phone. Your live availability, your services, your prices. No phone tag, no double bookings.'
      },
      {
        icon: 'PhoneCall',
        title: 'AI phone assistant',
        text: 'When you are with a client, it answers in a warm voice, checks your openings, and books the appointment right then. Every missed call becomes a booking instead of a voicemail.'
      },
      {
        icon: 'Bell',
        title: 'Reminders that cut no-shows',
        text: 'Automatic text and email reminders go out before every appointment, with one-tap confirm or reschedule. Fewer empty chairs and far fewer surprise gaps in your day.'
      },
      {
        icon: 'Repeat',
        title: 'Loyalty that drives rebooking',
        text: 'Points, rewards, and a gentle reminder that it is time for the next visit bring clients back on schedule. Turn a one-time facial into a standing appointment.'
      },
      {
        icon: 'Image',
        title: 'A portfolio that sells',
        text: 'A gorgeous gallery for your color work, balayage, nails, and spa space. Show the results that make people pick you, right on your own site.'
      },
      {
        icon: 'Star',
        title: 'More 5-star reviews',
        text: 'After every visit, happy clients get a friendly nudge to leave a Google review. You climb the local rankings so new clients find you first.'
      }
    ],
    stats: [
      { value: '40%', label: 'fewer no-shows with automatic reminders' },
      { value: '24/7', label: 'booking and calls answered, even mid-service' },
      { value: '3x', label: 'more 5-star reviews in 90 days' }
    ],
    quote: {
      text: 'I used to lose new clients every week because nobody could grab the phone mid-color. Now the AI books them while I work, the reminders cut our no-shows way down, and my chairs stay full on the slow days.',
      author: 'Marisa T.',
      role: 'Owner, Coastal Glow Salon & Spa'
    },
    cta: {
      title: 'Keep every chair full this season',
      text: 'See how Versa Launch books your clients around the clock, cuts your no-shows, and brings them back on schedule. Book a quick walkthrough built for your salon or spa.'
    }
  },

  {
    slug: 'gyms-studios',
    name: 'Gyms & studios',
    icon: 'Dumbbell',
    blurb: 'Turn trials into members and keep the ones you already have.',
    seoTitle: 'Gym & Studio Websites That Fill Classes - Versa Launch',
    seoDescription:
      'Turn free trials into members, win back quiet members, and fill off-peak classes. AI answers your phone, books signups, and asks for reviews. Built for gyms.',
    hero: {
      eyebrow: 'For gyms and studios',
      lead: 'Fill more classes and keep the members you',
      accent: 'already have',
      sub: 'A website that turns trials into signups, follow-up that wins members back before they cancel, and an AI assistant that answers the phone while you are on the floor.'
    },
    painsTitle: 'You know the leaks. We close them.',
    painsSub:
      'Most gyms and studios do not have a traffic problem. They have a follow-up problem. Here is where the money walks out the door.',
    pains: [
      {
        title: 'Trials sign up, then ghost',
        text: 'Someone books the free week, shows up once, and you never hear from them again. No text, no call, no reason to come back. The membership walks out with them.'
      },
      {
        title: 'Members cancel quietly',
        text: 'A regular stops showing for three weeks, then cancels. By the time you notice, it is too late. You never got the chance to pull them back.'
      },
      {
        title: 'The front desk is buried',
        text: 'The phone rings during a packed class, nobody can grab it, and the caller asks a competitor instead. Pricing and schedule questions eat the whole shift.'
      }
    ],
    solutionsTitle: 'One system to fill classes and keep members',
    solutionsSub:
      'It all works together. Sign up a trial, follow up on its own, book the class, win them back, and ask for the review. You run the gym. We run the follow-up.',
    solutions: [
      {
        icon: 'Calendar',
        title: 'Class schedule and signups',
        text: 'Members see the live schedule, reserve a spot, and join a membership right from their phone. No app store, no front desk line. The 2pm slot gets a nudge so it fills like the 6pm one.'
      },
      {
        icon: 'UserPlus',
        title: 'Free-trial and intro-offer capture',
        text: 'Your site turns visitors into trial signups with a clean intro offer. Every lead drops straight into follow-up so nobody slips away after the first visit.'
      },
      {
        icon: 'Repeat',
        title: 'Retention and win-back automations',
        text: 'When a member goes quiet, they get a real message that brings them back. Trials get a sequence that turns the free week into a paid membership instead of a ghost.'
      },
      {
        icon: 'Phone',
        title: 'AI phone assistant',
        text: 'It answers membership and pricing questions, books trials and classes, and captures every caller you miss during class. It replaces voicemail, so a busy floor never costs you a member.'
      },
      {
        icon: 'Smartphone',
        title: 'A branded member app feel',
        text: 'Members get a fast, branded home for their schedule, bookings, and account. It looks like your gym, runs on any phone, and there is nothing to download.'
      },
      {
        icon: 'Star',
        title: 'Automated review generation',
        text: 'After a great class or a hit milestone, members get asked for a Google review at the right moment. More 5-star reviews means more locals through the door.'
      }
    ],
    stats: [
      { value: '3x', label: 'more 5-star reviews in 90 days, typical' },
      { value: '40%', label: 'of free trials converted to paid, representative' },
      { value: '24/7', label: 'call answering so you never miss a signup' }
    ],
    quote: {
      text: 'We were losing trials the second they walked out the door. Now the follow-up happens on its own and our 9am classes are actually full. The phone gets answered even when all of us are coaching.',
      author: 'Marcus T.',
      role: 'Owner, Tidewater Strength Co.'
    },
    cta: {
      title: 'Stop losing members you already earned',
      text: 'See how Versa Launch fills your classes, wins back quiet members, and answers every call. Book a quick walkthrough built for your gym or studio.'
    }
  },

  {
    slug: 'professional-services',
    name: 'Professional services',
    icon: 'Briefcase',
    blurb: 'Look established, answer every inquiry, and turn consults into signed clients.',
    seoTitle: 'Technology for Law, Accounting & Consulting Firms - Versa Launch',
    seoDescription:
      'Versa Launch builds and manages the technology behind professional service firms. A credible website, an AI intake line, secure client portals, and local search that puts you in front of the right clients.',
    hero: {
      eyebrow: 'For professional services',
      lead: 'Win the client before the first',
      accent: 'meeting',
      sub: 'Attorneys, accountants, consultants, agents, and advisors. A site that reads credible, an intake line that never sends a prospect to voicemail, and a secure portal that makes working with you feel effortless.'
    },
    painsTitle: 'Your expertise is not the problem. Your first impression is',
    painsSub:
      'Prospects shopping for a professional are comparing three firms in one afternoon. Most of that comparison happens before anyone speaks to you.',
    pains: [
      {
        title: 'The site does not match the caliber of the work',
        text: 'A dated template with stock photos and a 2019 copyright makes a serious firm look small. Prospects assume the practice runs the way the website looks.'
      },
      {
        title: 'New inquiries hit voicemail during business hours',
        text: 'You are in a meeting, in court, or with a client. The prospect who finally worked up the nerve to call leaves no message and calls the next firm on the list.'
      },
      {
        title: 'Intake eats hours before anyone gets paid',
        text: 'Chasing documents, rescheduling consults, retyping the same details into three systems. That is billable time spent on administration nobody invoices for.'
      }
    ],
    solutionsTitle: 'The technology a serious practice should already have',
    solutionsSub:
      'Credibility up front, intake that runs itself, and secure handling of everything a client sends you.',
    solutions: [
      {
        icon: 'Globe',
        title: 'A site that signals credibility',
        text: 'Clean design, real bios, practice area pages written the way clients actually search, and results where they belong. It reads like a firm people trust with something important.'
      },
      {
        icon: 'PhoneCall',
        title: 'An AI intake line that never misses',
        text: 'Every inquiry gets answered on the first ring, screened against the matters you actually take, and either booked into a consult or escalated to you. Nights and weekends included.'
      },
      {
        icon: 'Lock',
        title: 'Secure client portals',
        text: 'Clients upload documents, sign forms, approve engagement letters, check status, and pay, all in a branded portal. Less email, fewer attachments floating around, cleaner records.'
      },
      {
        icon: 'CalendarCheck',
        title: 'Consults booked without the phone tag',
        text: 'Prospects see real availability and book themselves. Automatic reminders and confirmations cut the no-shows that quietly cost you a slot every week.'
      },
      {
        icon: 'Search',
        title: 'Found for what you actually practice',
        text: 'Local search built around your practice areas and the towns you serve, so you show up for the specific problem a client is searching at 11pm rather than a generic category.'
      },
      {
        icon: 'Star',
        title: 'Reviews that carry weight',
        text: 'Satisfied clients get asked at the right moment, in a way that respects confidentiality. A steady stream of recent reviews outweighs a competitor with a longer sign.'
      }
    ],
    stats: [
      { value: '24/7', label: 'inquiries answered, including nights and weekends' },
      { value: '2x', label: 'more consults booked from the same traffic, typical' },
      { value: 'Hours', label: 'of intake admin moved off billable time each week' }
    ],
    quote: {
      text: 'We were letting calls go to voicemail all day and hoping people would try again. They do not. The intake line books consults while we are with clients, and the portal ended the email chase for documents.',
      author: 'Alicia D.',
      role: 'Managing Partner, Harbor Point Law'
    },
    cta: {
      title: 'Make the first impression match the work',
      text: 'We will show you a new site, a live intake line, and a client portal set up for your practice. Book a short walkthrough and see it working.'
    }
  },

  {
    slug: 'multi-location',
    name: 'Multi-location businesses',
    icon: 'Building2',
    blurb: 'Consistent brand, per-location visibility, and one team behind every site.',
    seoTitle: 'Multi-Location Business Technology - Versa Launch',
    seoDescription:
      'Versa Launch manages technology across multiple locations: one brand system, per-location pages and listings, routed calls, and consolidated reporting. One vendor instead of one per site.',
    hero: {
      eyebrow: 'For multi-location businesses',
      lead: 'Every location running the same',
      accent: 'way',
      sub: 'Two locations or twenty. One brand system, a real page and listing for each site, calls routed to the right store, and one dashboard showing which locations are winning and which need help.'
    },
    painsTitle: 'Each location solved its own problem, and now nothing matches',
    painsSub:
      'Growth by location tends to produce growth by vendor. Different sites, different numbers, different listings, and no single view of any of it.',
    pains: [
      {
        title: 'Every location looks like a different company',
        text: 'One site was built by a nephew, one by an agency, one is a Facebook page. Hours conflict, branding drifts, and customers cannot tell which one is real.'
      },
      {
        title: 'Listings are wrong and nobody owns them',
        text: 'Old addresses, closed hours, duplicate profiles, and a phone number that rings a location you shut down. Google trusts none of it, so all of your locations rank lower.'
      },
      {
        title: 'You cannot compare locations honestly',
        text: 'One store says it is busy, another says the phone never rings. Without call data and lead tracking per location, staffing and marketing decisions are guesses.'
      }
    ],
    solutionsTitle: 'One system, every location',
    solutionsSub:
      'Central control where consistency matters, local flexibility where it actually helps.',
    solutions: [
      {
        icon: 'Globe',
        title: 'One brand, a page per location',
        text: 'A single site with a real page for every location, each with its own hours, staff, photos, reviews, and directions. Update the brand once and it propagates everywhere.'
      },
      {
        icon: 'MapPin',
        title: 'Listings managed per location',
        text: 'A clean Google Business Profile for each site, with duplicates removed, categories set, hours accurate, and posts running. Each location competes in its own map, not against your other stores.'
      },
      {
        icon: 'PhoneCall',
        title: 'Calls routed to the right store',
        text: 'One number or many, routed by location, hours, and staffing. The AI receptionist answers everywhere at once and sends each lead to the store that can actually serve it.'
      },
      {
        icon: 'BarChart3',
        title: 'Reporting that compares locations',
        text: 'Calls, leads, bookings, reviews, and rankings side by side per location. You can see which site needs staffing, which needs marketing, and which is quietly your best.'
      },
      {
        icon: 'Users',
        title: 'Onboarding a new location fast',
        text: 'Opening number four should not restart the whole technology project. A new location gets its page, listing, number, and routing from the existing system in days.'
      },
      {
        icon: 'CheckCircle',
        title: 'One vendor, not one per site',
        text: 'Website, phones, AI, software, search, and support handled by the same team for every location. One invoice, one number to call, one group that knows the whole footprint.'
      }
    ],
    stats: [
      { value: '1', label: 'team behind every location instead of one vendor each' },
      { value: 'Days', label: 'to bring a new location fully online' },
      { value: '24/7', label: 'call coverage across every site at once' }
    ],
    quote: {
      text: 'By the third location we had three websites, four phone numbers, and no idea which store was actually producing. Putting it all under one system was the first time we could compare locations honestly.',
      author: 'Rachel M.',
      role: 'Operations Director, four-location retail group'
    },
    cta: {
      title: 'Get every location on the same system',
      text: 'Send us your list of locations and what each one is running today. We will map what consolidating looks like, in what order, and what it costs.'
    }
  }
];

export default INDUSTRIES;
